// Terminal transport selection — the single seam between terminalManager and
// where a session's bytes actually flow.
//
//   relay  → wss://<api>/api/terminal/ws/…      (api gateway, as always)
//   direct → ws://127.0.0.1:<port>/terminal/…   (desktop local gateway: the
//            Electron main process SSHes straight to the host)
//
// Both endpoints speak the identical binary frame protocol, so everything
// else in terminalManager (partysocket reconnect, ready gating, ACK flow
// control) is transport-agnostic. Direct is the product default on desktop;
// 'auto' (explicit per-host setting) may fall back to relay once; 'relay'
// forces the gateway. The web build has no local gateway → always relay.

import { getBearer, isDesktop } from './desktop-token';
import { useAppStore } from '../store';
import { useSettings } from '../stores/settingsStore';

export type TerminalTransport = 'direct' | 'relay';

export function connectionModeFor(
    hostId: string,
): 'relay' | 'direct' | 'auto' {
    // The desktop-wide preference (Settings → General → Connection) is the
    // control surface. A host row explicitly set to 'relay' (API-only in v1 —
    // no per-host UI) is a server-side override: it forces relay here AND
    // blocks credential release on the api.
    const host = useAppStore.getState().hosts.find((h) => h.id === hostId);
    if (host?.connectionMode === 'relay') return 'relay';
    return useSettings.getState().desktopConnectionMode;
}

export function resolveTransport(hostId: string): TerminalTransport {
    if (!isDesktop() || !window.cotty?.localGateway) return 'relay';
    return connectionModeFor(hostId) === 'relay' ? 'relay' : 'direct';
}

export function buildWsUrl(
    transport: TerminalTransport,
    hostId: string,
    sessionId: string,
    cols: number,
    rows: number,
): string {
    if (transport === 'direct') {
        const gw = window.cotty!.localGateway!;
        const url = new URL(
            `ws://127.0.0.1:${gw.port}/terminal/${hostId}/${sessionId}`,
        );
        url.searchParams.set('cols', String(cols));
        url.searchParams.set('rows', String(rows));
        url.searchParams.set('t', gw.token);
        return url.toString();
    }
    const apiBase =
        (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ??
        window.location.origin;
    const wsBase = apiBase.replace(/^http/, 'ws');
    const url = new URL(`${wsBase}/api/terminal/ws/${hostId}/${sessionId}`);
    url.searchParams.set('cols', String(cols));
    url.searchParams.set('rows', String(rows));
    // Desktop has no session cookie on the WS upgrade — pass the bearer token as
    // `?t=`; the api promotes it to an Authorization header (verifyTerminalAuth).
    const token = getBearer();
    if (isDesktop() && token) url.searchParams.set('t', token);
    return url.toString();
}
